/**
 * ADMIN — Gestion des accès : Administrateurs, Rôles d'équipe (prédéfinis + personnalisés),
 * Journal des accès (filtrable) et courriel de test. Base MongoDB `users`.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { AdminSpaceEditor } from "../components/AdminSpaceEditor";
import { RoleAccessMatrix } from "../components/access/RoleAccessMatrix";
import {
  ShieldCheck, Plus, Trash, CircleNotch, Lock, User, EnvelopeSimple, X,
  UsersThree, ClockCounterClockwise, ArrowRight, SlidersHorizontal, PaperPlaneTilt, PencilSimple,
  DownloadSimple, Table, CalendarBlank, Hourglass, MagnifyingGlass, ClockClockwise, Monitor, DeviceMobile, CheckCircle,
} from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { value: "coach", label: "Coach" },
  { value: "reception", label: "Réception" },
  { value: "comptable", label: "Comptable" },
  { value: "marketing", label: "Marketing" },
];
const ROLE_BADGE = {
  coach: "bg-emerald-500/15 text-emerald-400",
  reception: "bg-sky-500/15 text-sky-400",
  comptable: "bg-violet-500/15 text-violet-400",
  marketing: "bg-pink-500/15 text-pink-400",
  custom: "bg-amber-500/15 text-amber-400",
};
// Miroir de NAV_GROUPS (pôles). admin_only = contient des outils réservés admin.
const ACCESS_GROUPS = [
  { id: "pilotage", label: "Accueil & pilotage" },
  { id: "dojo", label: "Dojo & élèves" },
  { id: "competition", label: "Compétition & événements" },
  { id: "crm", label: "CRM & relations" },
  { id: "marketing", label: "Marketing & contenu" },
  { id: "finance", label: "Finance & administration" },
  { id: "boutique", label: "Boutique & monétisation", admin_only: true },
  { id: "patrimoine", label: "Patrimoine & sinistre (privé)", admin_only: true },
  { id: "systeme", label: "IA, système & support", admin_only: true },
];
const GROUP_LABEL = Object.fromEntries(ACCESS_GROUPS.map((g) => [g.id, g.label]));

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("fr-CA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminsModule() {
  const { user: me } = useAuth();
  const isOwner = !!me?.is_owner;
  const [admins, setAdmins] = useState([]);
  const [team, setTeam] = useState([]);
  const [log, setLog] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: "", full_name: "", expires_at: "" });

  // Modale membre : mode = 'role' (prédéfini) ou 'custom'
  const [teamModal, setTeamModal] = useState(null); // {mode, email, full_name, role, groups, expires_at, editing}

  // Filtres du journal
  const [fActor, setFActor] = useState("");
  const [fAction, setFAction] = useState("");
  const [matrixSearch, setMatrixSearch] = useState("");
  const [extendModal, setExtendModal] = useState(null); // {email, date}
  const [historyModal, setHistoryModal] = useState(null); // {email, data, loading}
  const [blockedDevices, setBlockedDevices] = useState([]);
  const [showScore, setShowScore] = useState(false);
  const [smsTesting, setSmsTesting] = useState(false);
  const [smsModal, setSmsModal] = useState(null); // {numbers: [], input: ""}

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t, l, mx, sec, bd] = await Promise.all([
        api.get("/auth/admins"), api.get("/auth/team"), api.get("/auth/access-log"), api.get("/auth/access-matrix"), api.get("/auth/security-overview"), api.get("/auth/blocked-devices"),
      ]);
      setAdmins(a.data.admins || []);
      setTeam(t.data.team || []);
      setLog(l.data.entries || []);
      setMatrix(mx.data.matrix || []);
      setSecurity(sec.data || null);
      setBlockedDevices(bd.data.accounts || []);
    } catch { toast.error("Impossible de charger les accès."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grantAdmin = async () => {
    const email = (adminForm.email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Courriel invalide."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/admins", { email, full_name: adminForm.full_name, base_url: baseUrl, expires_at: adminForm.expires_at });
      toast.success(`${data.created ? "Compte admin créé" : "Droits admin accordés"} : ${email}. Courriel d'invitation envoyé.`);
      setAdminForm({ email: "", full_name: "", expires_at: "" }); setShowAddAdmin(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
    finally { setSaving(false); }
  };

  const revokeAdmin = async (email) => {
    if (!window.confirm(`Retirer les droits administrateur de ${email} ?`)) return;
    try { await api.delete(`/auth/admins/${encodeURIComponent(email)}`); toast.success(`Retiré : ${email}.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };

  const saveTeam = async () => {
    const m = teamModal; if (!m) return;
    const email = (m.email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Courriel invalide."); return; }
    setSaving(true);
    try {
      if (m.mode === "custom") {
        if (!(m.groups || []).length) { toast.error("Coche au moins un pôle."); setSaving(false); return; }
        const { data } = await api.post("/auth/team/custom", { email, full_name: m.full_name, groups: m.groups, base_url: baseUrl, expires_at: m.expires_at || "" });
        toast.success(`Accès personnalisé (${data.allowed_groups.length} pôle(s)) pour ${email}. Courriel envoyé.`);
      } else {
        const { data } = await api.post("/auth/team/assign", { email, full_name: m.full_name, role: m.role, base_url: baseUrl, expires_at: m.expires_at || "" });
        toast.success(`Rôle « ${data.role_label} » pour ${email}. Courriel envoyé.`);
      }
      setTeamModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
    finally { setSaving(false); }
  };

  const quickChangeRole = async (email, full_name, role) => {
    try {
      await api.post("/auth/team/assign", { email, full_name, role, base_url: baseUrl, notify: false });
      toast.success(`Rôle mis à jour pour ${email}.`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };

  const removeTeam = async (email) => {
    if (!window.confirm(`Retirer l'accès de ${email} ? Le compte redevient simple membre.`)) return;
    try { await api.delete(`/auth/team/${encodeURIComponent(email)}`); toast.success(`Accès retiré : ${email}.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };

  const sendTest = async () => {
    const to = window.prompt("Envoyer un courriel de test à :", "impactkb.bjj@gmail.com");
    if (!to) return;
    try {
      const { data } = await api.post("/auth/test-email", { to });
      toast.success(`Courriel de test envoyé à ${data.to} (via ${data.sender}).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/auth/access-log/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "journal-acces-kaizen.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Journal exporté (CSV).");
    } catch { toast.error("Échec de l'export."); }
  };

  const doExtend = async (email, { days, expires_at }) => {
    try {
      await api.post("/auth/access/extend", { email, days: days || 0, expires_at: expires_at || "" });
      toast.success(expires_at === "" && !days ? `Accès de ${email} rendu permanent.` : `Accès de ${email} prolongé.`);
      setExtendModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };

  const openHistory = async (email) => {
    setHistoryModal({ email, data: null, loading: true });
    try {
      const { data } = await api.get(`/auth/login-history?email=${encodeURIComponent(email)}`);
      setHistoryModal({ email, data, loading: false });
    } catch { setHistoryModal({ email, data: null, loading: false }); toast.error("Historique indisponible."); }
  };

  const toggle2fa = async (val) => {
    try {
      await api.put("/auth/settings", { admin_2fa_enabled: val });
      toast.success(val ? "2FA par courriel activée pour les admins." : "2FA désactivée.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const setReminderDays = async (days) => {
    try {
      await api.put("/auth/settings", { expiry_reminder_days: Number(days) });
      toast.success(`Rappel d'expiration réglé à ${days} jours.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const blockDevice = async (email, device) => {
    if (!device) return;
    if (!window.confirm(`Bloquer cet appareil pour ${email} ? Toute connexion future depuis cet appareil sera refusée.`)) return;
    try {
      await api.post("/auth/block-device", { email, device });
      toast.success("Appareil bloqué.");
      openHistory(email); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const unblockDevice = async (email, device) => {
    if (!window.confirm(`Débloquer cet appareil pour ${email} ? Les connexions depuis cet appareil seront de nouveau autorisées.`)) return;
    try {
      await api.post("/auth/unblock-device", { email, device });
      toast.success("Appareil débloqué.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const testSecuritySms = async () => {
    setSmsTesting(true);
    try {
      const { data } = await api.post("/auth/security-sms/test");
      toast.success(`SMS de test envoyé à ${(data.to || []).join(", ")}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi SMS."); }
    finally { setSmsTesting(false); }
  };
  const openSmsModal = () => setSmsModal({ numbers: [...(security?.sms_to || [])], input: "" });
  const addSmsNumber = () => {
    const n = (smsModal.input || "").trim();
    if (!n) return;
    const digits = n.replace(/[^\d+]/g, "");
    if (digits.replace("+", "").length < 10) { toast.error("Numéro trop court (min. 10 chiffres)."); return; }
    if (smsModal.numbers.includes(digits)) { toast.error("Numéro déjà ajouté."); return; }
    if (smsModal.numbers.length >= 5) { toast.error("Maximum 5 numéros."); return; }
    setSmsModal((m) => ({ ...m, numbers: [...m.numbers, digits], input: "" }));
  };
  const removeSmsNumber = (n) => setSmsModal((m) => ({ ...m, numbers: m.numbers.filter((x) => x !== n) }));
  const saveSmsNumbers = async () => {
    try {
      await api.put("/auth/settings", { security_sms_numbers: smsModal.numbers });
      toast.success("Numéros d'alerte SMS enregistrés.");
      setSmsModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const [backupModal, setBackupModal] = useState(null);
  const genBackupCodes = async () => {
    if (!window.confirm("Régénérer les codes de secours ? Les anciens seront invalidés.")) return;
    try {
      const { data } = await api.post("/auth/backup-codes/generate");
      setBackupModal({ codes: data.codes });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const sendDigestNow = async () => {
    try {
      const { data } = await api.post("/auth/security-digest/send");
      toast.success(data.sent ? "Résumé envoyé au propriétaire." : "Envoi impossible pour l'instant.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const setGeoPolicy = async (geo_policy) => {
    try { await api.put("/auth/settings", { geo_policy }); toast.success("Restriction géographique mise à jour."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const forceLogout = async (email) => {
    if (!window.confirm(`Déconnecter TOUTES les sessions de ${email} ? La personne devra se reconnecter.`)) return;
    try { await api.post("/auth/force-logout", { email }); toast.success(`Sessions de ${email} déconnectées.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };

  const actors = useMemo(() => Array.from(new Set(log.map((e) => e.actor).filter(Boolean))), [log]);
  const actions = useMemo(() => {
    const seen = {}; log.forEach((e) => { if (e.action) seen[e.action] = e.action_label; });
    return Object.entries(seen);
  }, [log]);
  const filteredLog = useMemo(() => log.filter((e) =>
    (!fActor || e.actor === fActor) && (!fAction || e.action === fAction)), [log, fActor, fAction]);

  const inputCls = "w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-600";

  const LastLogin = ({ v }) => (
    <span className={`text-[11px] ${v ? "text-zinc-500" : "text-zinc-600 italic"}`}>
      {v ? `Dernière connexion : ${fmtDate(v)}` : "Jamais connecté"}
    </span>
  );
  const ExpiryBadge = ({ v }) => {
    if (!v) return null;
    const past = new Date(v) < new Date();
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${past ? "bg-red-500/15 text-red-400" : "bg-orange-500/15 text-orange-400"}`}>
        <Hourglass size={11} /> {past ? "Expiré" : `Expire le ${fmtDate(v)}`}
      </span>
    );
  };

  return (
    <div className="space-y-8" data-testid="admins-module">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
            <ShieldCheck size={26} className="text-amber-400" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Gestion des accès</h1>
            <p className="text-sm text-zinc-400">Administrateurs, rôles d'équipe et historique des changements.</p>
          </div>
        </div>
        <button data-testid="test-email-btn" onClick={sendTest}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-medium transition-colors">
          <PaperPlaneTilt size={16} /> Courriel de test
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500"><CircleNotch size={28} className="animate-spin" /></div>
      ) : (
        <>
          {/* SÉCURITÉ & CONNEXIONS */}
          {security && (
            <section className="space-y-3" data-testid="security-section">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-base font-semibold text-zinc-200 flex items-center gap-2"><Monitor size={18} className="text-red-400" /> Sécurité & connexions</h2>
                <button type="button" onClick={() => setShowScore(true)} className="flex items-center gap-2 group" data-testid="security-score">
                  <span className="text-xs text-zinc-500 group-hover:text-zinc-300">Score de sécurité</span>
                  <span className={`px-2.5 py-1 rounded-lg text-sm font-bold ${(security.score ?? 0) >= 8 ? "bg-green-500/15 text-green-400" : (security.score ?? 0) >= 5 ? "bg-orange-500/15 text-orange-400" : "bg-red-500/15 text-red-400"}`}>{security.score ?? 0}/10</span>
                  <span className="text-[11px] text-zinc-500 underline decoration-dotted group-hover:text-zinc-300">détail</span>
                </button>
              </div>
              {(security.recommendations || []).length > 0 && (
                <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3" data-testid="security-recs">
                  <div className="text-xs font-medium text-orange-300 mb-1">Recommandations pour améliorer ton score :</div>
                  <ul className="text-xs text-zinc-400 list-disc list-inside space-y-0.5">
                    {security.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3">
                {/* Toggle 2FA */}
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-zinc-200">2FA courriel (admins)</div>
                    <p className="text-xs text-zinc-500 mt-1">Code par courriel à chaque connexion admin.</p>
                  </div>
                  <button data-testid="toggle-2fa" onClick={() => toggle2fa(!security.settings.admin_2fa_enabled)}
                    className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${security.settings.admin_2fa_enabled ? "bg-green-600" : "bg-zinc-700"}`}>
                    <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all ${security.settings.admin_2fa_enabled ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
                {/* Geo */}
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
                  <div className="text-sm font-medium text-zinc-200">Restriction géographique</div>
                  <p className="text-xs text-zinc-500 mt-1 mb-2">Connexions hors {(security.settings.allowed_countries || ["CA"]).join(", ")}.</p>
                  <div className="flex gap-1.5">
                    {[["off", "Désactivé"], ["alert", "Alerter"], ["block", "Bloquer"]].map(([v, lbl]) => (
                      <button key={v} data-testid={`geo-${v}`} onClick={() => setGeoPolicy(v)}
                        className={`px-2 py-1.5 rounded-lg text-xs font-medium ${security.settings.geo_policy === v ? "bg-red-600 text-red-50" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
                {/* Reminder days */}
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
                  <div className="text-sm font-medium text-zinc-200 flex items-center gap-2"><Hourglass size={15} className="text-orange-400" /> Rappel d'expiration</div>
                  <p className="text-xs text-zinc-500 mt-1 mb-2">Jours avant l'échéance.</p>
                  <div className="flex gap-2">
                    {[3, 7, 14].map((d) => (
                      <button key={d} data-testid={`reminder-${d}`} onClick={() => setReminderDays(d)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium ${security.settings.expiry_reminder_days === d ? "bg-orange-600 text-orange-50" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>{d} j</button>
                    ))}
                  </div>
                </div>
                {/* Backup codes + digest */}
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-2">
                  <button data-testid="gen-backup-btn" onClick={genBackupCodes}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium"><ShieldCheck size={14} /> Codes de secours</button>
                  <button data-testid="send-digest-btn" onClick={sendDigestNow}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium"><EnvelopeSimple size={14} /> M'envoyer le résumé</button>
                  <button data-testid="test-sms-btn" onClick={testSecuritySms} disabled={smsTesting || !security.sms_configured}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                    {smsTesting ? <CircleNotch size={14} className="animate-spin" /> : <DeviceMobile size={14} />} {security.sms_configured ? "Tester l'alerte SMS" : "SMS non configuré"}</button>
                  <button data-testid="manage-sms-btn" onClick={openSmsModal}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium"><SlidersHorizontal size={14} /> Gérer les numéros SMS</button>
                </div>
              </div>
              {security.sms_configured ? (
                <div className="text-[11px] text-zinc-500 flex items-center gap-1.5" data-testid="sms-status">
                  <DeviceMobile size={13} className="text-green-400" /> Alertes SMS actives → {(security.sms_to || []).join(", ")}
                </div>
              ) : (
                <div className="text-[11px] text-zinc-500 flex items-center gap-1.5" data-testid="sms-status">
                  <DeviceMobile size={13} className="text-zinc-600" /> Aucun numéro d'alerte SMS — clique « Gérer les numéros SMS » pour en ajouter.
                </div>
              )}
              {/* Tableau connexions admins */}
              <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="security-table">
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-zinc-900/60 text-[11px] uppercase tracking-wide text-zinc-500">
                  <div className="col-span-4">Compte</div><div className="col-span-3">Dernière connexion</div>
                  <div className="col-span-3">Dernier appareil</div><div className="col-span-2 text-right">Appareils</div>
                </div>
                {security.admins.map((r) => (
                  <div key={r.email} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-t border-zinc-800 text-xs items-center hover:bg-zinc-900/40">
                    <div className="col-span-4 truncate text-zinc-200">{r.email}</div>
                    <div className="col-span-3 text-zinc-400">{fmtDate(r.last_login) || <span className="italic text-zinc-600">Jamais</span>}</div>
                    <div className="col-span-3 text-zinc-500 truncate">{r.last_device || "—"} {r.last_ip ? `· ${r.last_ip}` : ""}</div>
                    <div className="col-span-2 text-right">
                      <span className="text-zinc-400">{r.device_count}</span>
                      {r.new_device_7d > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400">{r.new_device_7d} nouv.</span>}
                      {r.blocked_count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">{r.blocked_count} bloq.</span>}
                    </div>
                  </div>
                ))}
              </div>
              {/* Appareils bloqués */}
              <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="blocked-devices-section">
                <div className="px-4 py-2 bg-zinc-900/60 text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-2">
                  <Lock size={13} className="text-red-400" /> Appareils bloqués
                </div>
                {blockedDevices.length === 0 ? (
                  <div className="px-4 py-4 text-center text-xs text-zinc-500" data-testid="no-blocked-devices">Aucun appareil bloqué.</div>
                ) : (
                  blockedDevices.map((acc) => (
                    <div key={acc.email} className="px-4 py-2.5 border-t border-zinc-800 text-xs">
                      <div className="text-zinc-300 font-medium mb-1.5">{acc.email}</div>
                      <div className="flex flex-wrap gap-2">
                        {(acc.devices || []).map((dev) => (
                          <span key={dev} className="inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-red-500/10 text-red-300 font-mono">
                            {dev.slice(0, 12)}…
                            <button data-testid={`unblock-${dev.slice(0,8)}`} onClick={() => unblockDevice(acc.email, dev)}
                              className="px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-green-600/30 text-zinc-300 hover:text-green-300 font-sans">Débloquer</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          )}

          {/* ADMINISTRATEURS */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-base font-semibold text-zinc-200 flex items-center gap-2"><ShieldCheck size={18} className="text-amber-400" /> Administrateurs</h2>
              {isOwner && (
                <button data-testid="add-admin-btn" onClick={() => setShowAddAdmin(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-amber-50 text-sm font-medium transition-colors">
                  <Plus size={16} /> Ajouter un admin
                </button>
              )}
            </div>
            <p className="text-xs text-zinc-500">Les administrateurs atterrissent dans l'<b>Espace Administrateur</b> (pages choisies par le CEO ci-dessous). Seul le propriétaire <span className="inline-flex items-center gap-1 text-zinc-400"><Lock size={11} /> protégé</span> garde l'accès complet.</p>
            <AdminSpaceEditor isOwner={isOwner} />
            <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="admins-list">
              {admins.map((a) => (
                <div key={a.email} className="flex items-center justify-between gap-4 px-4 py-3 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900/40">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center shrink-0"><User size={18} className="text-zinc-400" /></div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-100 truncate">{a.email}</span>
                        {a.is_self && <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400">Toi</span>}
                        {a.protected && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-zinc-700/50 text-zinc-300"><Lock size={11} /> protégé</span>}
                        {a.source === "db" && <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400">ajouté</span>}
                        <ExpiryBadge v={a.access_expires_at} />
                      </div>
                      <LastLogin v={a.last_login} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`history-${a.email}`} onClick={() => openHistory(a.email)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium"><Monitor size={14} /> Historique</button>
                    <button data-testid={`logout-${a.email}`} onClick={() => forceLogout(a.email)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium">Déconnecter</button>
                    {!a.protected && a.access_expires_at && (
                      <button data-testid={`extend-${a.email}`} onClick={() => setExtendModal({ email: a.email, date: (a.access_expires_at || "").slice(0, 10) })}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 text-xs font-medium"><ClockClockwise size={14} /> Prolonger</button>
                    )}
                    {a.protected || a.is_self ? (
                      <span className="text-xs text-zinc-600">{a.is_self ? "—" : <Lock size={16} />}</span>
                    ) : (
                      <button data-testid={`revoke-admin-${a.email}`} onClick={() => revokeAdmin(a.email)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-medium"><Trash size={15} /> Retirer</button>
                    )}
                  </div>
                </div>
              ))}
              {admins.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-500">Aucun administrateur.</div>}
            </div>
          </section>

          {/* ÉQUIPE & RÔLES */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-base font-semibold text-zinc-200 flex items-center gap-2"><UsersThree size={18} className="text-emerald-400" /> Équipe & rôles</h2>
              <button data-testid="add-team-btn" onClick={() => setTeamModal({ mode: "role", email: "", full_name: "", role: "coach", groups: [], expires_at: "" })}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-emerald-50 text-sm font-medium transition-colors">
                <Plus size={16} /> Ajouter un membre
              </button>
            </div>
            <p className="text-xs text-zinc-500">Rôle prédéfini (coach, réception…) ou <strong className="text-amber-400/90">accès personnalisé</strong> : coche exactement les pôles visibles.</p>
            <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="team-list">
              {team.map((m) => (
                <div key={m.email} className="flex items-center justify-between gap-4 px-4 py-3 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900/40">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center shrink-0"><User size={18} className="text-zinc-400" /></div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-100 truncate">{m.email}</span>
                        <span className={`text-[11px] px-1.5 py-0.5 rounded ${ROLE_BADGE[m.role] || "bg-zinc-700/50 text-zinc-300"}`}>{m.role_label || m.role}</span>
                        {m.role === "custom" && <span className="text-[11px] text-amber-400/80">{(m.allowed_groups || []).map((g) => GROUP_LABEL[g] || g).join(" · ")}</span>}
                        <ExpiryBadge v={m.access_expires_at} />
                      </div>
                      <LastLogin v={m.last_login} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {m.role === "custom" ? (
                      <button data-testid={`edit-custom-${m.email}`}
                        onClick={() => setTeamModal({ mode: "custom", email: m.email, full_name: m.full_name, role: "coach", groups: m.allowed_groups || [], expires_at: (m.access_expires_at || "").slice(0, 10), editing: true })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs font-medium"><PencilSimple size={15} /> Pôles</button>
                    ) : (
                      <select data-testid={`role-select-${m.email}`} value={m.role} onChange={(e) => quickChangeRole(m.email, m.full_name, e.target.value)}
                        className="rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-200 outline-none">
                        {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    )}
                    <button data-testid={`extend-${m.email}`} onClick={() => setExtendModal({ email: m.email, date: (m.access_expires_at || "").slice(0, 10) })}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 text-xs font-medium"><ClockClockwise size={14} /> Prolonger</button>
                    <button data-testid={`remove-team-${m.email}`} onClick={() => removeTeam(m.email)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-medium"><Trash size={15} /> Retirer</button>
                  </div>
                </div>
              ))}
              {team.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-500">Aucun membre d'équipe.</div>}
            </div>
          </section>

          <RoleAccessMatrix team={team} />

          {/* JOURNAL DES ACCÈS */}
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-zinc-200 flex items-center gap-2"><ClockCounterClockwise size={18} className="text-zinc-400" /> Journal des accès</h2>
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={15} className="text-zinc-500" />
                <select data-testid="filter-actor" value={fActor} onChange={(e) => setFActor(e.target.value)}
                  className="rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-200 outline-none">
                  <option value="">Tous les auteurs</option>
                  {actors.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <select data-testid="filter-action" value={fAction} onChange={(e) => setFAction(e.target.value)}
                  className="rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-200 outline-none">
                  <option value="">Toutes les actions</option>
                  {actions.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                </select>
                <button data-testid="export-log-btn" onClick={exportCsv}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium">
                  <DownloadSimple size={15} /> CSV
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="access-log">
              {filteredLog.map((e, i) => (
                <div key={i} className="flex items-start justify-between gap-4 px-4 py-2.5 border-b border-zinc-800 last:border-b-0 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${e.action.includes("revoke") || e.action.includes("remove") ? "bg-red-500/15 text-red-400" : (e.action.includes("device_block") || e.action.includes("force_logout")) ? "bg-orange-500/15 text-orange-400" : "bg-green-500/15 text-green-400"}`}>{e.action_label}</span>
                    <span className="text-zinc-300 truncate">{e.summary}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500 shrink-0">
                    <span className="hidden sm:inline">{e.actor}</span><ArrowRight size={12} className="hidden sm:inline" /><span>{fmtDate(e.created_at)}</span>
                  </div>
                </div>
              ))}
              {filteredLog.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-500">Aucune entrée.</div>}
            </div>
          </section>

          {/* QUI VOIT QUOI */}
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-zinc-200 flex items-center gap-2"><Table size={18} className="text-sky-400" /> Qui voit quoi</h2>
              <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3">
                <MagnifyingGlass size={15} className="text-zinc-500" />
                <input data-testid="matrix-search" type="text" value={matrixSearch} onChange={(e) => setMatrixSearch(e.target.value)}
                  placeholder="Rechercher une personne…" className="bg-transparent py-1.5 text-xs text-zinc-100 outline-none placeholder:text-zinc-600 w-48" />
                {matrixSearch && <button onClick={() => setMatrixSearch("")} className="text-zinc-500 hover:text-zinc-300"><X size={14} /></button>}
              </div>
            </div>
            {matrixSearch ? (
              <p className="text-xs text-zinc-400" data-testid="matrix-search-summary">
                {(() => {
                  const s = matrixSearch.toLowerCase();
                  const hits = matrix.filter((g) => g.people.some((p) => p.email.toLowerCase().includes(s)));
                  return hits.length ? `${matrixSearch} a accès à ${hits.length} pôle(s) : ${hits.map((g) => g.label).join(", ")}` : `Aucun accès trouvé pour « ${matrixSearch} ».`;
                })()}
              </p>
            ) : (
              <p className="text-xs text-zinc-500">Pour chaque pôle, les personnes qui y ont accès (audit rapide).</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="access-matrix">
              {matrix
                .filter((g) => !matrixSearch || g.people.some((p) => p.email.toLowerCase().includes(matrixSearch.toLowerCase())))
                .map((g) => {
                  const people = matrixSearch ? g.people.filter((p) => p.email.toLowerCase().includes(matrixSearch.toLowerCase())) : g.people;
                  return (
                    <div key={g.group_id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-zinc-200">{g.label}</span>
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-700/50 text-zinc-300">{g.count}</span>
                      </div>
                      <div className="space-y-1">
                        {people.map((p) => (
                          <div key={p.email} className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-zinc-400 truncate">{p.email}</span>
                            <span className={`shrink-0 px-1.5 py-0.5 rounded ${ROLE_BADGE[p.kind === "Personnalisé" ? "custom" : ""] || "bg-zinc-800 text-zinc-400"}`}>{p.kind}</span>
                          </div>
                        ))}
                        {people.length === 0 && <div className="text-xs text-zinc-600 italic">Personne</div>}
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>
        </>
      )}

      {/* Modale admin */}
      {showAddAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowAddAdmin(false)}>
          <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-950 p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="add-admin-modal">
            <div className="flex items-center justify-between"><h2 className="text-lg font-bold text-zinc-100">Ajouter un administrateur</h2>
              <button onClick={() => setShowAddAdmin(false)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button></div>
            <p className="text-xs text-zinc-500">Un courriel de bienvenue avec lien de connexion sera envoyé automatiquement.</p>
            <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3">
              <EnvelopeSimple size={16} className="text-zinc-500" />
              <input data-testid="admin-email-input" type="email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder="nom@exemple.com" className="flex-1 bg-transparent py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-600" /></div>
            <input data-testid="admin-name-input" type="text" value={adminForm.full_name} onChange={(e) => setAdminForm({ ...adminForm, full_name: e.target.value })} placeholder="Nom (optionnel)" className={inputCls} />
            <label className="block">
              <span className="text-xs text-zinc-400 flex items-center gap-1"><CalendarBlank size={13} /> Expiration de l'accès (optionnel)</span>
              <input data-testid="admin-expiry-input" type="date" value={adminForm.expires_at} onChange={(e) => setAdminForm({ ...adminForm, expires_at: e.target.value })} className={`mt-1 ${inputCls}`} />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAddAdmin(false)} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm text-zinc-300">Annuler</button>
              <button data-testid="confirm-add-admin-btn" onClick={grantAdmin} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-amber-50 text-sm font-medium disabled:opacity-60">
                {saving ? <CircleNotch size={16} className="animate-spin" /> : <Plus size={16} />} Accorder l'accès admin</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale membre (rôle prédéfini ou personnalisé) */}
      {teamModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setTeamModal(null)}>
          <div className="w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-950 p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-team-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-zinc-100">{teamModal.editing ? "Modifier l'accès" : "Ajouter un membre d'équipe"}</h2>
              <button onClick={() => setTeamModal(null)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button></div>

            {/* Choix du mode */}
            <div className="flex gap-2">
              <button data-testid="mode-role-btn" onClick={() => setTeamModal({ ...teamModal, mode: "role" })} disabled={teamModal.editing}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${teamModal.mode === "role" ? "bg-emerald-600 text-emerald-50" : "bg-zinc-800 text-zinc-300"} disabled:opacity-40`}>Rôle prédéfini</button>
              <button data-testid="mode-custom-btn" onClick={() => setTeamModal({ ...teamModal, mode: "custom" })}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${teamModal.mode === "custom" ? "bg-amber-600 text-amber-50" : "bg-zinc-800 text-zinc-300"}`}>Accès personnalisé</button>
            </div>

            {!teamModal.editing && (
              <>
                <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3">
                  <EnvelopeSimple size={16} className="text-zinc-500" />
                  <input data-testid="team-email-input" type="email" value={teamModal.email} onChange={(e) => setTeamModal({ ...teamModal, email: e.target.value })}
                    placeholder="nom@exemple.com" className="flex-1 bg-transparent py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-600" /></div>
                <input data-testid="team-name-input" type="text" value={teamModal.full_name} onChange={(e) => setTeamModal({ ...teamModal, full_name: e.target.value })} placeholder="Nom (optionnel)" className={inputCls} />
              </>
            )}
            {teamModal.editing && <div className="text-sm text-zinc-400">{teamModal.email}</div>}

            {teamModal.mode === "role" ? (
              <div>
                <span className="text-xs text-zinc-400">Rôle</span>
                <select data-testid="team-role-select" value={teamModal.role} onChange={(e) => setTeamModal({ ...teamModal, role: e.target.value })} className={`mt-1 ${inputCls}`}>
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            ) : (
              <div className="space-y-2" data-testid="custom-groups">
                <span className="text-xs text-zinc-400">Pôles visibles pour cette personne</span>
                <div className="grid grid-cols-1 gap-1.5">
                  {ACCESS_GROUPS.map((g) => {
                    const checked = (teamModal.groups || []).includes(g.id);
                    return (
                      <label key={g.id} data-testid={`group-${g.id}`}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${checked ? "border-amber-600/60 bg-amber-500/10" : "border-zinc-800 bg-zinc-900"}`}>
                        <input type="checkbox" checked={checked}
                          onChange={(e) => {
                            const set = new Set(teamModal.groups || []);
                            e.target.checked ? set.add(g.id) : set.delete(g.id);
                            setTeamModal({ ...teamModal, groups: Array.from(set) });
                          }} className="accent-amber-500" />
                        <span className="text-sm text-zinc-200">{g.label}</span>
                        {g.admin_only && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-zinc-700/60 text-zinc-400">outils admin</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <label className="block">
              <span className="text-xs text-zinc-400 flex items-center gap-1"><CalendarBlank size={13} /> Expiration de l'accès (optionnel)</span>
              <input data-testid="team-expiry-input" type="date" value={teamModal.expires_at || ""} onChange={(e) => setTeamModal({ ...teamModal, expires_at: e.target.value })} className={`mt-1 ${inputCls}`} />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setTeamModal(null)} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm text-zinc-300">Annuler</button>
              <button data-testid="confirm-team-btn" onClick={saveTeam} disabled={saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60 ${teamModal.mode === "custom" ? "bg-amber-600 hover:bg-amber-500 text-amber-50" : "bg-emerald-600 hover:bg-emerald-500 text-emerald-50"}`}>
                {saving ? <CircleNotch size={16} className="animate-spin" /> : <Plus size={16} />} {teamModal.editing ? "Enregistrer" : "Assigner"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Codes de secours */}
      {backupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setBackupModal(null)}>
          <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-950 p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="backup-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-2"><ShieldCheck size={18} className="text-amber-400" /> Codes de secours</h2>
              <button onClick={() => setBackupModal(null)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
            </div>
            <p className="text-xs text-orange-300">Note ces codes maintenant : ils ne seront plus jamais affichés. Chaque code fonctionne une seule fois à l'étape de vérification, si tu n'as pas accès à tes courriels.</p>
            <div className="grid grid-cols-2 gap-2" data-testid="backup-codes-list">
              {backupModal.codes.map((c) => (
                <div key={c} className="font-mono text-center text-sm text-zinc-100 bg-zinc-900 border border-zinc-800 rounded-lg py-2 tracking-widest">{c}</div>
              ))}
            </div>
            <button onClick={() => { navigator.clipboard?.writeText(backupModal.codes.join("\n")); toast.success("Codes copiés."); }}
              className="w-full px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm text-zinc-200">Copier tous les codes</button>
          </div>
        </div>
      )}

      {/* Modale Prolonger */}
      {extendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setExtendModal(null)}>
          <div className="w-full max-w-sm rounded-xl border border-zinc-800 bg-zinc-950 p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="extend-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-2"><ClockClockwise size={18} className="text-orange-400" /> Prolonger l'accès</h2>
              <button onClick={() => setExtendModal(null)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
            </div>
            <p className="text-xs text-zinc-500">{extendModal.email}</p>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="extend-30" onClick={() => doExtend(extendModal.email, { days: 30 })} className="px-2 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-200">+30 jours</button>
              <button data-testid="extend-90" onClick={() => doExtend(extendModal.email, { days: 90 })} className="px-2 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-200">+90 jours</button>
              <button data-testid="extend-365" onClick={() => doExtend(extendModal.email, { days: 365 })} className="px-2 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-200">+1 an</button>
            </div>
            <label className="block">
              <span className="text-xs text-zinc-400 flex items-center gap-1"><CalendarBlank size={13} /> Ou une date précise</span>
              <input data-testid="extend-date-input" type="date" value={extendModal.date} onChange={(e) => setExtendModal({ ...extendModal, date: e.target.value })} className={`mt-1 ${inputCls}`} />
            </label>
            <div className="flex items-center justify-between gap-2 pt-1">
              <button data-testid="extend-permanent" onClick={() => doExtend(extendModal.email, { expires_at: "" })} className="text-xs text-green-400 hover:text-green-300 underline">Rendre permanent</button>
              <button data-testid="extend-confirm" onClick={() => doExtend(extendModal.email, { expires_at: extendModal.date })} disabled={!extendModal.date}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-orange-50 text-sm font-medium disabled:opacity-50">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Historique de connexion */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setHistoryModal(null)}>
          <div className="w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-950 p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="history-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-2"><Monitor size={18} className="text-sky-400" /> Historique de connexion</h2>
              <button onClick={() => setHistoryModal(null)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
            </div>
            <p className="text-xs text-zinc-500">{historyModal.email}</p>
            {historyModal.loading ? (
              <div className="flex items-center justify-center py-10 text-zinc-500"><CircleNotch size={24} className="animate-spin" /></div>
            ) : historyModal.data ? (
              <>
                <div className="text-xs text-zinc-400">{historyModal.data.device_count} appareil(s) connu(s) · Dernière connexion : {fmtDate(historyModal.data.last_login) || "—"}</div>
                <div className="rounded-lg border border-zinc-800 divide-y divide-zinc-800">
                  {(historyModal.data.logins || []).map((l, i) => (
                    <div key={i} className="px-3 py-2 text-xs flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-zinc-300">{fmtDate(l.at)}</div>
                        <div className="text-zinc-500 truncate">{l.ua}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-zinc-500">{l.ip_prefix}</span>
                        {l.new_device && <span className="px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400">nouvel appareil</span>}
                        {l.dh && (
                          <button data-testid={`block-device-${i}`} onClick={() => blockDevice(historyModal.email, l.dh)}
                            className="px-2 py-0.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400">Bloquer</button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(historyModal.data.logins || []).length === 0 && <div className="px-3 py-6 text-center text-xs text-zinc-500">Aucune connexion enregistrée pour l'instant.</div>}
                </div>
              </>
            ) : (
              <div className="py-8 text-center text-sm text-zinc-500">Historique indisponible.</div>
            )}
          </div>
        </div>
      )}

      {showScore && security && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowScore(false)}>
          <div className="w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-950 p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="score-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-2"><ShieldCheck size={18} className="text-green-400" /> Détail du score de sécurité</h2>
              <button onClick={() => setShowScore(false)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1.5 rounded-lg text-lg font-bold ${(security.score ?? 0) >= 8 ? "bg-green-500/15 text-green-400" : (security.score ?? 0) >= 5 ? "bg-orange-500/15 text-orange-400" : "bg-red-500/15 text-red-400"}`}>{security.score ?? 0}/10</span>
              <p className="text-xs text-zinc-500">Chaque critère ci-dessous ajoute des points à ton score. Corrige les critères en rouge pour l'améliorer.</p>
            </div>
            <div className="rounded-lg border border-zinc-800 divide-y divide-zinc-800" data-testid="score-breakdown">
              {(security.score_breakdown || []).map((b, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {b.ok ? <CheckCircle size={18} weight="fill" className="text-green-400 shrink-0" /> : <X size={18} className="text-red-400 shrink-0" />}
                    <span className={`truncate ${b.ok ? "text-zinc-300" : "text-zinc-400"}`}>{b.label}</span>
                  </div>
                  <span className={`shrink-0 font-mono text-xs px-2 py-0.5 rounded ${b.ok ? "bg-green-500/15 text-green-400" : "bg-zinc-800 text-zinc-500"}`}>{b.points}/{b.max} pts</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {smsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSmsModal(null)}>
          <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-950 p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="sms-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-2"><DeviceMobile size={18} className="text-green-400" /> Numéros d'alerte SMS</h2>
              <button onClick={() => setSmsModal(null)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
            </div>
            <p className="text-xs text-zinc-500">Ces numéros reçoivent un SMS lors d'une connexion suspecte (nouvel appareil ou pays inhabituel). Max 5 numéros. Format international recommandé (ex. +1 819 527 8489).</p>
            <div className="flex gap-2">
              <input data-testid="sms-input" value={smsModal.input} onChange={(e) => setSmsModal((m) => ({ ...m, input: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") addSmsNumber(); }}
                className={inputCls} placeholder="+1 819 555 1234" />
              <button data-testid="sms-add-btn" onClick={addSmsNumber}
                className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-green-50 text-sm font-medium"><Plus size={16} /></button>
            </div>
            <div className="space-y-1.5">
              {smsModal.numbers.length === 0 && <div className="text-xs text-zinc-600 italic py-2">Aucun numéro pour l'instant.</div>}
              {smsModal.numbers.map((n) => (
                <div key={n} className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800" data-testid={`sms-num-${n}`}>
                  <span className="text-sm text-zinc-200 font-mono">{n}</span>
                  <button data-testid={`sms-remove-${n}`} onClick={() => removeSmsNumber(n)} className="text-red-400 hover:text-red-300"><Trash size={16} /></button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setSmsModal(null)} className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm">Annuler</button>
              <button data-testid="sms-save-btn" onClick={saveSmsNumbers} className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-green-50 text-sm font-medium">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
